<template>
    <v-row dense id="sale_payments_page">
        <v-col cols="12">
            <!--Datatable Toolbar-->
            <v-toolbar flat color="white" elevation="1" class="custom_datatable_toolbar" height="56">
                <v-toolbar-title>{{$t('sale_payments.list')}}</v-toolbar-title>
                <v-spacer></v-spacer>
                <v-btn v-if="canCreateItem && !view_mode && $store.state.can_create_payment" color="primary" dark
                       class="custom_button mobile_button" @click="openItemDialog('create', 0, 0)"
                       :title="$t('sale_payments.create')">
                    <v-icon small size="20">add</v-icon>
                </v-btn>
            </v-toolbar>

            <!--Datatable-->
            <ResponsiveDataTable :headers="headers" :items="items" :loading="loading" :mobileBreakpoint="960"
                                 :sortByColumn="'3'">
                <!--Buttons-->
                <template v-slot:buttons="{item, item_index}">
                    <div class="actions">
                        <v-icon v-if="canViewItem && !view_mode" color="primary" :title="$t('general.view')"
                                @click="openItemDialog('view', item)">view_list
                        </v-icon>
                        <v-icon v-if="canUpdateItem && !view_mode" color="primary" :title="$t('general.update')"
                                @click="openItemDialog('update', item)">
                            edit
                        </v-icon>
                        <v-icon v-if="canDeleteItem && !view_mode" color="primary" :title="$t('general.delete')"
                                @click="showDeleteItemDialog(item)">delete
                        </v-icon>
                    </div>
                </template>
            </ResponsiveDataTable>
        </v-col>

        <!--Item Dialog-->
        <ManageSalePaymentDialog :show="show_item_dialog" :item_header="item_dialog_header"
                                 :is_loading="item_dialog_loading" :item_action="item_action" :item_data="item"
                                 @close-dialog="closeItemDialog" @submit-dialog="submitItemDialog">
        </ManageSalePaymentDialog>

        <!--Delete item dialog-->
        <StatusDialog :show="show_delete_item_dialog" :header="$t('sale_payments.delete')" color="error"
                      :text="$t('sale_payments.delete_confirmation')" :is_loading="delete_item_dialog_loading"
                      @confirm="deleteItem" @close="closeDeleteItemDialog">
        </StatusDialog>

    </v-row>
</template>

<script>
    import ResponsiveDataTable from '../components/ResponsiveDataTable.vue'
    import ManageSalePaymentDialog from '../components/ManageSalePaymentDialog.vue'
    import StatusDialog from '../components/StatusDialog.vue'

    export default {
        props: {
            list_items: {type: Array, default: () => []},
            sale_id: {type: Number, default: 0},
            view_mode: {type: Boolean, default: false}
        },
        components: {
            ResponsiveDataTable, ManageSalePaymentDialog, StatusDialog
        },
        data: self => ({
            url: 'sale_payments',

            // actions permissions
            canViewItem: false,
            canCreateItem: false,
            canUpdateItem: false,
            canDeleteItem: false,

            // datatable
            headers: [],
            items: [],
            loading: false,

            item: null,
            item_obj: {
                amount: null,
                date: null,
                id: null,
                payment_type_id: null,
                payment_type_name: null
            },
            item_id: null,
            item_index: null,

            // manage item
            show_item_dialog: false,
            item_dialog_header: null,
            item_dialog_loading: false,
            item_action: null,

            // delete item
            show_delete_item_dialog: false,
            delete_item_dialog_loading: false
        }),
        computed: {
            locale() {
                return this.$i18n.locale;
            },
            isSuperAdmin() {
                return this.$store.state.user_details.roles.map(obj => obj.name).indexOf('superadmin') > -1;
            }
        },
        created() {
            this.headers = this.getHeaders();
        },
        async mounted() {
            this.canCreateItem = await this.$store.dispatch('hasPermission', 'create_sale');
            this.canViewItem = await this.$store.dispatch('hasPermission', 'view_sale');
            this.canUpdateItem = await this.$store.dispatch('hasPermission', 'update_sale');
            this.canDeleteItem = await this.$store.dispatch('hasPermission', 'delete_sale');
        },
        watch: {
            locale(val) {
                this.headers = this.getHeaders();
            },
            list_items(val) {
                // after submit sale dialog
                if (val.length === 0) {

                    this.items = [];

                    // view or update from sale dialog
                } else {

                    val.forEach(payment => {
                        let obj = {};

                        // populate payment obj
                        for (let prop in this.item_obj) {
                            obj[prop] = payment[prop];
                        }

                        // push obj to items array
                        this.items.push(obj);
                    });
                }
            }
        },
        methods: {
            // return headers array
            getHeaders() {
                return [
                    {text: `${this.$t('sale_payments.date')}`, value: 'date', sortable: true},
                    {text: `${this.$t('general.amount')}`, value: 'amount', sortable: true},
                    {text: `${this.$t('general.payment_type')}`, value: 'payment_type_name', sortable: true},
                    {text: `${this.$t('datatable.actions')}`, value: 'actions', align: 'right', sortable: false}
                ]
            },

            // Manage Item
            openItemDialog(action, prop) {
                this.item_dialog_header = (action === 'create') ? this.$t('sale_payments.create')
                    : (action === 'update') ? this.$t('sale_payments.update') : this.$t('sale_payments.view');
                this.item_id = prop ? prop.item.id : 0;
                this.item_index = prop ? prop.item_index : 0;
                this.item_action = action;
                this.item = prop.item;
                this.show_item_dialog = true;
            },
            // clear item dialog related data
            clearItemDialog() {
                this.item_dialog_header = null;
                this.item_id = null;
                this.item_action = null;
            },
            // close item dialog
            closeItemDialog() {
                this.show_item_dialog = false;
                this.clearItemDialog();
            },

            // submit item dialog
            submitItemDialog(item, action) {
                this.closeItemDialog();

                // create
                if (action === 'create') {
                    this.items.push(item);

                    // update
                } else if (action === 'update') {

                    let obj = {};

                    for (let prop in this.item_obj) {
                        obj[prop] = item[prop];
                    }

                    // replace obj in the given index
                    this.items.splice(this.item_index, 1, obj);
                }

                // trigger event to change date in parent component
                this.$emit('items-changed', this.items)
            },

            // Delete item
            // show delete item dialog
            showDeleteItemDialog(prop) {
                this.show_delete_item_dialog = true;
                this.item_index = prop.item_index;
            },
            // close delete item dialog
            closeDeleteItemDialog() {
                this.show_delete_item_dialog = false;
                this.item_id = null;
            },
            // delete item
            async deleteItem() {
                this.delete_item_dialog_loading = true;

                // remove item from array
                this.items.splice(this.item_index, 1);

                // trigger event to change date in parent component
                this.$emit('items-changed', this.items);

                // close dialog
                this.closeDeleteItemDialog();

                this.delete_item_dialog_loading = false;
            }
        }
    }
</script>